import React, { useEffect, useMemo, useState } from "react";
import { FaTimes, FaFolderOpen, FaSync, FaDownload, FaEnvelope, FaFilePdf, FaExpand, FaCompress, FaExternalLinkAlt } from "react-icons/fa";
import { SCHREIBEN_API, api, blobDownload } from "./schreibenUtils";
import StammdatenAnlegenDialog from "../../../components/stammdaten/StammdatenAnlegenDialog";
import "./UmsatzEmpfaenger.css";

/* Welle 125 — Vorgang „Firmenwechsel": alle Vertragspartner informieren, dass ihre Verträge auf eine andere Firma
   übergehen (FBG → FVV). 1 Firmen & Anlage · 2 Empfänger (Umsätze + Verträge + Versicherungen) · 3 Text ·
   4 Erzeugen & senden (E-Mail je Empfänger). Rückfragen auf der Seite, keine Browser-Dialoge.
   Hotfix 6.20.1: E-Mail ist Pflicht (ohne → Fehler, nichts wird abgelegt), Schritte jederzeit zurück (Vorgang wird
   aktualisiert), aus der Vorgangsliste öffnet sich der Firmenwechsel wieder hier (vorgangId).
   Welle 132 (Meeting 14.10.): Vollbild / eigenes Fenster, Zeitraum + mind. Zahlungen ODER Einzelbetrag ab €,
   Liste mit Vertrag (Art, Nummer) und Zahlungen (Anzahl, Summe, größte, letzte), sortierbar, „nur Umsätze ohne Vertrag",
   Buchungen aufklappen, „als Vertrag anlegen", alle wählen/abwählen, drei Textvarianten je Empfänger. */
const SCHRITTE = ["Firmen & Anlage", "Empfänger", "Text", "Erzeugen & senden"];
const VARIANTEN = [["ummeldung", "Ummeldung"], ["abmeldung", "Abmeldung (Betrieb eingestellt)"], ["kuendigung", "Kündigung · Folgepächter TCH"]];
const VAR_KURZ = { ummeldung: "Ummeldung", abmeldung: "Abmeldung", kuendigung: "Kündigung" };
const MAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const mailOkFn = (m) => MAIL_RX.test((m || "").trim());
const ohneNamen = (l) => ["", "?"].includes(String(l || "").trim());
const WURZEL = "/Holthey Immobilien";
const heute = () => new Date().toISOString().slice(0, 10);
const schluessel = (e) => `${e.quelle}:${e.id}`;
const euro = (b) => (b === undefined || b === null || b === "") ? "" : Number(b).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const datumDe = (d) => (d ? d.split("-").reverse().join(".") : "");

export default function FirmenwechselAssistent({ onClose, onFertig, vorgangId = null, vollbildStart = false }) {
  const [schritt, setSchritt] = useState(0);
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");
  const [busy, setBusy] = useState(false);
  const [vollbild, setVollbild] = useState(vollbildStart);
  const [alt, setAlt] = useState("FBG");
  const [neu, setNeu] = useState("FVV");
  const [gueltig, setGueltig] = useState(heute());
  const [vorlage, setVorlage] = useState(null);
  const [konto, setKonto] = useState("");
  const [seit, setSeit] = useState("2026-01-01");          // Welle 132: Umsätze ab
  const [minAnzahl, setMinAnzahl] = useState(3);            // mind. Zahlungen …
  const [minBetrag, setMinBetrag] = useState(100);          // … oder einmal ab € (große Einzelzahlung)
  const [anlagen, setAnlagen] = useState([]);
  const [browser, setBrowser] = useState(null);
  const [liste, setListe] = useState(null);
  const [gewaehlt, setGewaehlt] = useState(new Set());
  const [mails, setMails] = useState({});               // korrigierte E-Mail je Empfänger
  const [namen, setNamen] = useState({});               // Name für Empfänger ohne Namen („?" aus den Umsätzen)
  const [varianten, setVarianten] = useState({});       // Textvariante je Empfänger
  const [filter, setFilter] = useState({ q: "", mail: "", herkunft: "", ohneVertrag: false, stillSeit: "" });
  const [sortierung, setSortierung] = useState({ feld: "", ab: true });
  const [offen, setOffen] = useState({});               // aufgeklappte Zeilen → Buchungen
  const [vertragNeu, setVertragNeu] = useState(null);   // Umsatz-Zeile, die als Vertrag angelegt wird
  const [setzVariante, setSetzVariante] = useState("abmeldung");
  const [texte, setTexte] = useState({});               // {variante: {betreff, text}}
  const [textReiter, setTextReiter] = useState("ummeldung");
  const [sid, setSid] = useState(null);
  const [ergebnis, setErgebnis] = useState(null);
  const [frage, setFrage] = useState(null);

  const texteAusVorlage = (v, vorhanden = {}) => {
    const t = v?.thema?.texte || {};
    const raus = {};
    VARIANTEN.forEach(([k]) => { raus[k] = vorhanden[k] || { betreff: t[k]?.betreff || "", text: t[k]?.text || "" }; });
    return raus;
  };

  const laden = async () => {
    setFehler("");
    try {
      const v = await api(`/firmenwechsel/vorlage?alt=${encodeURIComponent(alt)}&neu=${encodeURIComponent(neu)}`);
      setVorlage(v); setKonto((k) => k || v.konten?.[0]?.suchbegriff || "");
      setTexte((alt0) => texteAusVorlage(v, alt0));
    } catch (e) { setFehler(String(e.message || e)); setVorlage(null); }
  };
  useEffect(() => { if (!vorgangId) laden(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Bestehenden Firmenwechsel-Vorgang (aus der Vorgangsliste) wieder hier öffnen
  useEffect(() => {
    if (!vorgangId) return;
    (async () => {
      try {
        const d = await api(`/firmenwechsel/${vorgangId}`);
        const m = d.meta || {}; const v = d.vorgang || {};
        const a = m.alt || "FBG", n = m.neu || "FVV";
        setAlt(a); setNeu(n); if (m.gueltig_ab) setGueltig(m.gueltig_ab);
        setAnlagen((v.anlagen || []).map((x) => ({ path: x.pfad || x.path, name: x.name, groesse: x.groesse })));
        const empf = v.empfaenger || [];
        setListe({ empfaenger: empf, gespeichert: true });
        setGewaehlt(new Set(empf.map(schluessel)));
        setVarianten(Object.fromEntries(empf.map((e) => [schluessel(e), e.extra?.textvariante || "ummeldung"])));
        const st = Object.values(m.versand || {});
        if (st.length) setErgebnis({ stand: { mail: st.filter((x) => x.art === "mail").length, brief: st.filter((x) => x.art === "brief").length }, fehler: [], protokoll: [] });
        setSid(vorgangId); setSchritt(3);
        const vl = await api(`/firmenwechsel/vorlage?alt=${encodeURIComponent(a)}&neu=${encodeURIComponent(n)}`);
        setVorlage(vl); setKonto(vl.konten?.[0]?.suchbegriff || "");
        // gespeicherte Texte haben Vorrang; ältere Vorgänge (vor Welle 132) nur mit Haupttext
        const gesp = m.texte || { ummeldung: { betreff: v.betreff || "", text: v.text || "" } };
        setTexte(texteAusVorlage(vl, gesp));
      } catch (e) { setFehler(String(e.message || e)); }
    })();
  }, [vorgangId]);

  // Anlage (HR-Auszug) aus der Dropbox
  const browse = async (pfad) => {
    try { setBrowser(await api(`/anlagen/browse?path=${encodeURIComponent(pfad || WURZEL)}`)); }
    catch (e) { setFehler(String(e.message || e)); }
  };

  const empfaengerLaden = async () => {
    setBusy(true); setFehler("");
    try {
      const q = new URLSearchParams({ alt, konto, min_anzahl: String(minAnzahl || 1), seit: seit || "" });
      if (minBetrag !== "" && minBetrag !== null) q.set("min_betrag", String(minBetrag));
      const d = await api(`/firmenwechsel/empfaenger?${q.toString()}`);
      const alteWahl = liste ? new Set(gewaehlt) : null;
      if (liste?.gespeichert)   // im Vorgang eingetragene E-Mails behalten
        setMails((p) => ({ ...Object.fromEntries(liste.empfaenger.filter((e) => e.mail).map((e) => [schluessel(e), e.mail])), ...p }));
      setListe(d);
      setVarianten((p) => ({ ...Object.fromEntries(d.empfaenger.map((e) => [schluessel(e), e.extra?.textvariante || "ummeldung"])), ...p }));
      // Vorauswahl: alle mit E-Mail (Versand geht nur per E-Mail); bei einem bestehenden Vorgang die bisherige Auswahl
      setGewaehlt(alteWahl && alteWahl.size ? new Set(d.empfaenger.map(schluessel).filter((k) => alteWahl.has(k)))
        : new Set(d.empfaenger.filter((e) => mailOkFn(e.mail)).map(schluessel)));
      setOffen({});
      setSchritt(1);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const sichtbar = useMemo(() => {
    const l = (liste?.empfaenger || []).filter((e) => {
      const q = filter.q.trim().toLowerCase();
      const em = mails[schluessel(e)] ?? e.mail;
      if (filter.mail === "mit" && !mailOkFn(em)) return false;
      if (filter.mail === "ohne" && mailOkFn(em)) return false;
      if (filter.herkunft && !(e.herkunft || []).includes(filter.herkunft)) return false;
      if (filter.ohneVertrag && !e.umsatz_ohne_vertrag) return false;
      if (filter.stillSeit) {            // Verträge, die seit … nicht mehr bezahlt wurden (gekündigt?)
        if (!(e.vertrag_info || []).length) return false;
        if (e.umsatz?.zuletzt && e.umsatz.zuletzt >= filter.stillSeit) return false;
      }
      return !q || q.split(/\s+/).every((w) => `${e.label} ${(e.adressblock || []).join(" ")} ${e.mail || ""} ${e.vertraege || ""} ${e.vertragsnummer || ""}`.toLowerCase().includes(w));
    });
    const { feld, ab } = sortierung;
    if (!feld) return l;
    const wert = (e) => feld === "name" ? String(e.label || "").toLowerCase()
      : feld === "summe" ? Math.abs(e.umsatz?.summe || 0) : feld === "max" ? (e.umsatz?.max_betrag || 0)
      : feld === "anzahl" ? (e.umsatz?.anzahl || 0) : (e.umsatz?.zuletzt || "");
    return [...l].sort((a, b) => { const x = wert(a), y = wert(b); return (x < y ? -1 : x > y ? 1 : 0) * (ab ? 1 : -1); });
  }, [liste, filter, mails, sortierung]);
  const sortiere = (feld) => setSortierung((s) => ({ feld, ab: s.feld === feld ? !s.ab : feld === "name" }));
  const pfeil = (feld) => (sortierung.feld === feld ? (sortierung.ab ? " ▲" : " ▼") : "");
  const umschalten = (e) => setGewaehlt((p) => { const n = new Set(p); if (n.has(schluessel(e))) n.delete(schluessel(e)); else n.add(schluessel(e)); return n; });
  const alleWaehlen = (an) => setGewaehlt(an ? new Set((liste?.empfaenger || []).map(schluessel)) : new Set());
  const gefilterteWaehlen = () => setGewaehlt((p) => { const n = new Set(p); sichtbar.forEach((e) => n.add(schluessel(e))); return n; });
  const varianteFuerAuswahl = () => setVarianten((p) => { const n = { ...p }; gewaehlt.forEach((k) => { n[k] = setzVariante; }); return n; });

  const aufklappen = async (e) => {
    const k = schluessel(e);
    if (offen[k]) { setOffen((p) => { const n = { ...p }; delete n[k]; return n; }); return; }
    if (!e.umsatz?.schluessel?.length) { setOffen((p) => ({ ...p, [k]: { buchungen: [] } })); return; }
    setOffen((p) => ({ ...p, [k]: { laedt: true } }));
    try {
      const q = new URLSearchParams({ konto, seit: liste?.filter?.seit || seit || "" });
      e.umsatz.schluessel.forEach((s) => q.append("schluessel", s));
      const d = await api(`/firmenwechsel/buchungen?${q.toString()}`);
      setOffen((p) => ({ ...p, [k]: { buchungen: d.buchungen || [] } }));
    } catch (ex) { setOffen((p) => ({ ...p, [k]: { fehler: String(ex.message || ex) } })); }
  };

  const auswahl = (liste?.empfaenger || []).filter((e) => gewaehlt.has(schluessel(e)))
    .map((e) => {
      const k = schluessel(e);
      const label = ((namen[k] ?? e.label) || "").trim();
      const block = (e.adressblock || []).filter((z) => !ohneNamen(z));
      return { ...e, label, adressblock: block.length ? block : [label], mail: ((mails[k] ?? e.mail) || "").trim(), versand: "mail",
        extra: { ...(e.extra || {}), textvariante: varianten[k] || e.extra?.textvariante || "ummeldung" } };
    });
  const ohneMail = auswahl.filter((e) => !mailOkFn(e.mail));
  const ohneName = auswahl.filter((e) => ohneNamen(e.label));
  const jeVariante = Object.fromEntries(VARIANTEN.map(([k]) => [k, auswahl.filter((e) => e.extra.textvariante === k).length]));

  const exportieren = async () => {
    try {
      const empf = (liste?.empfaenger || []).map((e) => {
        const k = schluessel(e);
        return { ...e, label: (namen[k] ?? e.label), mail: (mails[k] ?? e.mail), gewaehlt: gewaehlt.has(k),
          extra: { ...(e.extra || {}), textvariante: varianten[k] || "ummeldung" } };
      });
      const r = await fetch(`${SCHREIBEN_API}/firmenwechsel/export`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ empfaenger: empf, titel: `Firmenwechsel ${alt} → ${neu}${sid ? ` · Vorgang #${sid}` : ""}` }) });
      if (!r.ok) throw new Error("Export fehlgeschlagen");
      await blobDownload(r, `Firmenwechsel ${alt}.xlsx`);
    } catch (e) { setFehler(String(e.message || e)); }
  };

  const neuesFenster = () => {
    const ziel = `${window.location.origin}${window.location.pathname}?tab=vorgaenge&firmenwechsel=${sid || "neu"}&vollbild=1`;
    const w = window.open("about:blank", "_blank");      // erst leer öffnen (Popup-Blocker), dann Ziel setzen
    if (w) { w.location.href = ziel; onClose && onClose(); } else window.location.href = ziel;
  };

  const anlegen = async () => {
    setFrage(null); setBusy(true); setFehler("");
    try {
      const body = { alt, neu, gueltig_ab: gueltig, betreff: texte.ummeldung?.betreff || "", text: texte.ummeldung?.text || "",
        texte, empfaenger: auswahl,
        anlagen: anlagen.map((a) => ({ quelle: "dropbox", pfad: a.path, name: a.name, groesse: a.groesse })) };
      const d = await api(sid ? `/firmenwechsel/${sid}` : "/firmenwechsel", "POST", body);
      setSchritt(3);
      setMeldung(sid ? `Vorgang #${d.id} aktualisiert (${auswahl.length} Empfänger).`
        : `Vorgang #${d.id} angelegt (${auswahl.length} Empfänger). Jetzt erzeugen & senden.`);
      setSid(d.id);
      onFertig && onFertig();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };
  const vorschau = async () => {
    try {
      const r = await fetch(`${SCHREIBEN_API}/${sid}/pdf?nur_vorschau=1`, { method: "POST" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Vorschau fehlgeschlagen");
      const url = URL.createObjectURL(await r.blob()); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setFehler(String(e.message || e)); }
  };
  const senden = async () => {
    setFrage(null); setBusy(true); setFehler("");
    try {
      const d = await api(`/firmenwechsel/${sid}/versand?mit_mail=1`, "POST");
      setErgebnis(d);
      setMeldung(`${d.mail.length} per E-Mail gesendet.` + (d.fehler.length ? ` Fehler: ${d.fehler.join(" · ")}` : ""));
      onFertig && onFertig();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };
  const mailOk = vorlage?.mail?.verfuegbar;
  // Jeder Schritt bleibt erreichbar (Hotfix 6.20.1) — Änderungen aktualisieren den bestehenden Vorgang
  const kannZu = (i) => i === 0 || (i === 1 && !!liste) || (i === 2 && !!liste && gewaehlt.size > 0) || (i === 3 && !!sid);
  const breiteStil = vollbild ? { width: "100vw", maxWidth: "100vw" } : undefined;
  // feste Referenz — ein neues Objekt je Render würde die Lade-Effekte der Maske endlos neu auslösen (vgl. Welle 131)
  const vorbelegungNeu = useMemo(() => (vertragNeu ? {
    absender: vertragNeu.umsatz?.name || vertragNeu.label, vertagspartner: alt,
    hinweis: `Aus dem Umsatz: ${vertragNeu.umsatz?.name || vertragNeu.label} · ${vertragNeu.umsatz?.anzahl || 0} Zahlungen · `
      + `IBAN ${vertragNeu.umsatz?.iban || "—"} · letzter Zweck: ${vertragNeu.umsatz?.zweck || "—"}` } : null), [vertragNeu, alt]);
  const t = texte[textReiter] || { betreff: "", text: "" };
  const setT = (feld, wert) => setTexte((p) => ({ ...p, [textReiter]: { ...(p[textReiter] || {}), [feld]: wert } }));

  return (
    <div className="me-drawer-overlay" onClick={vollbild ? undefined : onClose}>
      <div className="me-drawer vg-drawer" style={breiteStil} onClick={(e) => e.stopPropagation()}>
        <div className="me-drawer-header">
          <div>
            <h3>Firmenwechsel</h3>
            <span className="me-drawer-sub">{vorlage ? `${vorlage.alt.name} → ${vorlage.neu.name}` : `${alt} → ${neu}`}
              {sid ? ` · Vorgang #${sid}` : ""} · {gewaehlt.size} Empfänger</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="me-btn me-btn-icon" title={vollbild ? "schmal" : "Vollbild"} onClick={() => setVollbild((x) => !x)}>
              {vollbild ? <FaCompress /> : <FaExpand />}</button>
            <button className="me-btn me-btn-icon" title="In neuem Fenster öffnen" onClick={neuesFenster}><FaExternalLinkAlt /></button>
            <button className="me-btn me-btn-icon" title="schließen" onClick={onClose}><FaTimes /></button>
          </div>
        </div>
        <div className="me-drawer-body">
          <div className="vg-stepper">
            {SCHRITTE.map((s, i) => (
              <div key={s} className={`vg-step ${i === schritt ? "aktiv" : i < schritt ? "fertig" : ""}`}
                   style={{ cursor: kannZu(i) ? "pointer" : "default" }}
                   onClick={() => { if (kannZu(i)) setSchritt(i); }}>
                <span className="vg-step-nr">{i + 1}</span><span>{s}</span>
              </div>
            ))}
          </div>
          {fehler && <div className="me-status error">❌ {fehler}</div>}
          {meldung && <div className="me-status success">✅ {meldung}</div>}

          {schritt === 0 && (
            <div className="me-drawer-section">
              <h4>Firmen, Stichtag und Anlage</h4>
              <div className="vg-grid">
                <span>bisherige Firma</span>
                <input className="vg-in" value={alt} onChange={(e) => setAlt(e.target.value.toUpperCase())} onBlur={laden} />
                <span>neue Firma</span>
                <input className="vg-in" value={neu} onChange={(e) => setNeu(e.target.value.toUpperCase())} onBlur={laden} />
                <span>gültig ab</span>
                <input className="vg-in" type="date" value={gueltig} onChange={(e) => setGueltig(e.target.value)} />
                <span>Konto (Umsätze)</span>
                <select className="vg-in" value={konto} onChange={(e) => setKonto(e.target.value)}>
                  {(vorlage?.konten || []).map((k) => <option key={k.suchbegriff} value={k.suchbegriff}>{k.suchbegriff} · {k.inhaber} · bis {k.zuletzt}</option>)}
                  <option value="">— ohne Umsätze (nur Verträge) —</option>
                </select>
                <span>Umsätze ab</span>
                <input className="vg-in" type="date" value={seit} disabled={!konto} onChange={(e) => setSeit(e.target.value)} />
                <span>Auswahl</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="ue-klein">mind.</span>
                  <input className="vg-in" type="number" min={1} max={50} style={{ width: 70 }} value={minAnzahl} disabled={!konto}
                         onChange={(e) => setMinAnzahl(Math.max(1, Number(e.target.value) || 1))} />
                  <span className="ue-klein">Zahlungen oder einmal ab</span>
                  <input className="vg-in" type="number" min={0} step={50} style={{ width: 90 }} value={minBetrag} disabled={!konto}
                         onChange={(e) => setMinBetrag(e.target.value === "" ? "" : Number(e.target.value))} />
                  <span className="ue-klein">€</span>
                </div>
              </div>
              <div className="ue-klein" style={{ margin: "4px 0 8px" }}>
                Kleine Einmal- und Abo-Buchungen fallen weg; eine einzelne große Zahlung (z. B. eine Rate) bleibt drin.
              </div>
              {vorlage && (
                <div className="ue-klein" style={{ margin: "8px 0", lineHeight: 1.6 }}>
                  <b>{vorlage.neu.name}</b> · {vorlage.neu.strasse}, {vorlage.neu.plz_ort} · Geschäftsführung: {vorlage.neu.geschaeftsfuehrer || "—"}
                  <br />Konto: {vorlage.neu.bank}{vorlage.neu.handelsregister ? ` · ${vorlage.neu.handelsregister}` : " · Handelsregister noch nicht eingetragen (Lasche „Adressen“)"}
                </div>
              )}
              <h4 style={{ marginTop: 12 }}>Anlage (Handelsregisterauszug)</h4>
              {anlagen.map((a) => (
                <div key={a.path} className="ue-klein">📎 {a.name} <span style={{ cursor: "pointer", color: "#a5b4fc" }}
                  onClick={() => setAnlagen((p) => p.filter((x) => x.path !== a.path))}>entfernen</span></div>
              ))}
              <button className="me-btn me-btn-secondary me-btn-sm" onClick={() => browse(`${WURZEL}/Firmen`)}><FaFolderOpen /> aus Dropbox wählen</button>
              {browser && (
                <div className="me-table-wrap" style={{ maxHeight: 260, overflow: "auto", marginTop: 6 }}>
                  <div className="ue-klein" style={{ padding: 4 }}>{browser.pfad}
                    {browser.pfad !== WURZEL && <span style={{ cursor: "pointer", color: "#a5b4fc", marginLeft: 8 }}
                      onClick={() => browse(browser.pfad.replace(/\/[^/]+$/, "") || WURZEL)}>↑ eine Ebene hoch</span>}
                    <span style={{ cursor: "pointer", color: "#a5b4fc", marginLeft: 8 }} onClick={() => setBrowser(null)}>schließen</span></div>
                  <table className="me-table"><tbody>
                    {browser.ordner.map((o) => <tr key={o.path} style={{ cursor: "pointer" }} onClick={() => browse(o.path)}><td>📁 {o.name}</td></tr>)}
                    {browser.dateien.map((d) => <tr key={d.path} style={{ cursor: "pointer" }}
                      onClick={() => { setAnlagen((p) => [...p.filter((x) => x.path !== d.path), d]); setBrowser(null); }}><td>📄 {d.name}</td></tr>)}
                  </tbody></table>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <button className="me-btn me-btn-primary" disabled={busy || !vorlage || !gueltig} onClick={empfaengerLaden}>
                  {busy ? "sammelt Empfänger …" : "Weiter → Empfänger"}</button>
              </div>
            </div>
          )}

          {schritt === 1 && liste && (
            <div className="me-drawer-section">
              <h4>Empfänger — {gewaehlt.size} gewählt</h4>
              <div className="ue-klein" style={{ marginBottom: 6 }}>
                {liste.gespeichert
                  ? <>Empfänger dieses Vorgangs. <span style={{ cursor: "pointer", color: "#a5b4fc" }} onClick={empfaengerLaden}>Alle Vertragspartner von {alt} neu laden</span></>
                  : <>{liste.empfaenger.length} Vertragspartner aus Umsätzen ({konto || "—"}{liste.filter?.seit ? ` ab ${datumDe(liste.filter.seit)}` : ""}, mind. {liste.filter?.min_anzahl}× oder einmal ab {euro(liste.filter?.min_betrag)} €), Verträgen und Versicherungen von {alt}.</>}
                {" "}<b>Versand nur per E-Mail — jeder gewählte Empfänger braucht eine E-Mail-Adresse.</b>
              </div>
              <div className="ue-leiste">
                <input className="me-filter-input" style={{ flex: "1 1 220px" }} placeholder="Filtern (Name, Anschrift, Vertragsnummer …)" value={filter.q}
                       onChange={(e) => setFilter({ ...filter, q: e.target.value })} />
                <select className="vg-in" value={filter.mail} onChange={(e) => setFilter({ ...filter, mail: e.target.value })}>
                  <option value="">alle</option><option value="mit">mit E-Mail</option><option value="ohne">ohne E-Mail</option>
                </select>
                <select className="vg-in" value={filter.herkunft} onChange={(e) => setFilter({ ...filter, herkunft: e.target.value })}>
                  <option value="">Umsatz + Vertrag + Versicherung</option><option>Umsatz</option><option>Vertrag</option><option>Versicherung</option>
                </select>
                <label className="ue-check"><input type="checkbox" checked={filter.ohneVertrag}
                  onChange={(e) => setFilter({ ...filter, ohneVertrag: e.target.checked })} /> nur Umsätze ohne Vertrag</label>
                <label className="ue-check" title="Verträge, die seit diesem Datum nicht mehr bezahlt wurden (gekündigt?)">Verträge ohne Zahlung seit
                  <input className="vg-in" type="date" style={{ width: 140 }} value={filter.stillSeit}
                         onChange={(e) => setFilter({ ...filter, stillSeit: e.target.value })} /></label>
              </div>
              <div className="ue-leiste">
                <button className="me-btn me-btn-secondary me-btn-sm" onClick={() => alleWaehlen(true)}>alle wählen ({liste.empfaenger.length})</button>
                <button className="me-btn me-btn-secondary me-btn-sm" onClick={() => alleWaehlen(false)}>alle abwählen</button>
                <button className="me-btn me-btn-secondary me-btn-sm" disabled={sichtbar.length === liste.empfaenger.length}
                        onClick={gefilterteWaehlen}>gefilterte wählen ({sichtbar.length})</button>
                <span className="ue-klein" style={{ marginLeft: 8 }}>Text für Auswahl:</span>
                <select className="vg-in" value={setzVariante} onChange={(e) => setSetzVariante(e.target.value)}>
                  {VARIANTEN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <button className="me-btn me-btn-secondary me-btn-sm" disabled={!gewaehlt.size} onClick={varianteFuerAuswahl}>setzen ({gewaehlt.size})</button>
                <button className="me-btn me-btn-secondary me-btn-sm" onClick={exportieren}><FaDownload /> Übersicht als Excel</button>
              </div>
              <div className="me-table-wrap" style={{ maxHeight: vollbild ? "calc(100vh - 360px)" : 440, overflow: "auto" }}>
                <table className="me-table fw-tabelle" style={{ tableLayout: "fixed", width: "100%", minWidth: 980 }}>
                  <colgroup><col style={{ width: 30 }} /><col style={{ width: "24%" }} /><col style={{ width: "17%" }} /><col style={{ width: 150 }} />
                    <col style={{ width: 90 }} /><col /><col style={{ width: 150 }} /></colgroup>
                  <thead><tr>
                    <th></th>
                    <th style={{ cursor: "pointer" }} onClick={() => sortiere("name")}>Empfänger{pfeil("name")}</th>
                    <th>Vertrag</th>
                    <th style={{ cursor: "pointer" }} onClick={() => sortiere("summe")} title="Klick: nach Summe, nochmal: umdrehen">
                      Zahlungen{pfeil("summe")}{pfeil("max")}<span className="ue-klein" style={{ display: "block", fontWeight: 400 }}>
                        <span onClick={(ev) => { ev.stopPropagation(); sortiere("max"); }} style={{ textDecoration: "underline" }}>nach größter</span>
                        {" · "}<span onClick={(ev) => { ev.stopPropagation(); sortiere("anzahl"); }} style={{ textDecoration: "underline" }}>Anzahl{pfeil("anzahl")}</span></span></th>
                    <th style={{ cursor: "pointer" }} onClick={() => sortiere("zuletzt")}>letzte{pfeil("zuletzt")}</th>
                    <th>Anschrift · E-Mail</th>
                    <th>Text</th>
                  </tr></thead>
                  <tbody>
                  {sichtbar.map((e) => {
                    const k = schluessel(e);
                    const em = mails[k] ?? e.mail ?? "";
                    const ok = mailOkFn(em);
                    const [l, f] = ok ? ["E-Mail", "#16a34a"] : ["E-Mail fehlt", "#d97706"];
                    const rot = gewaehlt.has(k) && !ok;
                    const u = e.umsatz || null;
                    const o = offen[k];
                    return (
                      <React.Fragment key={k}>
                      <tr>
                        <td style={{ width: 30 }}><input type="checkbox" checked={gewaehlt.has(k)} onChange={() => umschalten(e)} /></td>
                        <td style={{ minWidth: 0 }}>
                          {ohneNamen(e.label)
                            ? <input className="vg-in" style={{ width: "100%", borderColor: gewaehlt.has(k) && ohneNamen(namen[k]) ? "#f87171" : undefined }}
                                     placeholder="Name (Pflicht — unbekannt aus den Umsätzen)" value={namen[k] ?? ""}
                                     onChange={(ev) => setNamen((p) => ({ ...p, [k]: ev.target.value }))} />
                            : <b>{e.label}</b>}
                          <div><span className="ue-badge" style={{ borderColor: f, color: f }}>{l}</span>
                          <span className="ue-klein"> {(e.herkunft || []).join(" + ")}</span></div></td>
                        <td style={{ minWidth: 0 }}>
                          {(e.vertrag_info || []).length
                            ? (e.vertrag_info || []).map((v) => <div key={v.id} className="ue-klein"><b>{v.art || "Vertrag"}</b> {v.nummer || "—"}{v.objekt ? ` · ${v.objekt}` : ""}</div>)
                            : e.umsatz_ohne_vertrag
                              ? <div><span className="ue-badge" style={{ borderColor: "#f87171", color: "#f87171" }}>kein Vertrag</span>
                                  <div><span style={{ cursor: "pointer", color: "#a5b4fc", fontSize: 12 }} onClick={() => setVertragNeu(e)}>+ als Vertrag anlegen</span></div></div>
                              : <span className="ue-klein">{e.vertraege || ""}</span>}
                        </td>
                        <td>{u ? <div className="ue-klein" style={{ cursor: "pointer" }} onClick={() => aufklappen(e)} title="Buchungen anzeigen">
                              {o ? "▾" : "▸"} <b>{u.anzahl}×</b> · {euro(u.summe)} €<br />größte {euro(u.max_betrag)} €</div>
                            : <span className="ue-klein">—</span>}</td>
                        <td className="ue-klein">{u ? datumDe(u.zuletzt) : "keine"}</td>
                        <td style={{ minWidth: 0 }}><div className="ue-klein">{(e.adressblock || []).join(", ")}</div>
                          <input className="vg-in" style={{ width: "100%", marginTop: 4, borderColor: rot ? "#f87171" : undefined }}
                                 placeholder="E-Mail (Pflicht)" value={em}
                                 onChange={(ev) => setMails((p) => ({ ...p, [k]: ev.target.value.trim() }))} /></td>
                        <td><select className="vg-in" style={{ width: "100%" }} value={varianten[k] || "ummeldung"}
                                    onChange={(ev) => setVarianten((p) => ({ ...p, [k]: ev.target.value }))}>
                          {VARIANTEN.map(([vk]) => <option key={vk} value={vk}>{VAR_KURZ[vk]}</option>)}</select></td>
                      </tr>
                      {o && (
                        <tr><td></td><td colSpan={6}>
                          {o.laedt ? <span className="ue-klein">lädt Buchungen …</span>
                            : o.fehler ? <span style={{ color: "#f87171" }}>{o.fehler}</span>
                            : !(o.buchungen || []).length ? <span className="ue-klein">keine Buchungen im Zeitraum</span>
                            : <table className="me-table" style={{ width: "100%" }}><tbody>
                                {o.buchungen.map((b, i) => <tr key={i}><td className="ue-klein" style={{ width: 90 }}>{datumDe(b.tag)}</td>
                                  <td className="ue-klein" style={{ width: 100, textAlign: "right" }}>{euro(b.betrag)} €</td>
                                  <td className="ue-klein">{b.name} · {b.zweck}</td></tr>)}
                              </tbody></table>}
                        </td></tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody></table>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                <button className="me-btn me-btn-secondary" onClick={() => setSchritt(0)}>← Zurück</button>
                <button className="me-btn me-btn-primary" disabled={!gewaehlt.size || ohneMail.length > 0 || ohneName.length > 0}
                  onClick={() => setSchritt(2)}>Weiter → Text ({gewaehlt.size})</button>
              </div>
              {ohneMail.length > 0 && (
                <div className="me-status error" style={{ marginTop: 8 }}>
                  E-Mail fehlt bei {ohneMail.length} gewählten Empfänger(n):{" "}
                  {ohneMail.slice(0, 6).map((e) => e.label || "ohne Namen").join(", ")}{ohneMail.length > 6 ? " …" : ""} — bitte eintragen oder abwählen.
                </div>
              )}
              {ohneName.length > 0 && (
                <div className="me-status error" style={{ marginTop: 8 }}>
                  Name fehlt bei {ohneName.length} gewählten Empfänger(n) (aus den Umsätzen nicht bekannt) — bitte Namen eintragen oder abwählen.
                </div>
              )}
            </div>
          )}

          {schritt === 2 && (
            <div className="me-drawer-section">
              <h4>Text</h4>
              <div className="ue-leiste">
                {VARIANTEN.map(([k, l]) => (
                  <button key={k} className={`me-btn me-btn-sm ${textReiter === k ? "me-btn-primary" : "me-btn-secondary"}`}
                          onClick={() => setTextReiter(k)}>{l} ({jeVariante[k] || 0})</button>
                ))}
              </div>
              <div className="ue-klein" style={{ marginBottom: 6 }}>Platzhalter: {"{alte_firma} {neue_firma} {neue_anschrift} {gueltig_ab} {geschaeftsfuehrer} {bank} {handelsregister}"} — je Empfänger: {"{anrede} {vertrag_satz} {vertrag_bezug} {unterschrift}"}</div>
              <div className="vg-grid">
                <span>Betreff</span><input className="vg-in" value={t.betreff} onChange={(e) => setT("betreff", e.target.value)} />
              </div>
              {/* .vg-in hat eine feste Höhe von 32 px (Eingabefelder) — beim Textfeld überschreiben */}
              <textarea className="vg-in" rows={18} style={{ width: "100%", marginTop: 8, fontFamily: "inherit", height: "auto",
                minHeight: 380, padding: "8px 10px", lineHeight: 1.45, resize: "vertical" }} value={t.text} onChange={(e) => setT("text", e.target.value)} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                <button className="me-btn me-btn-secondary" onClick={() => setSchritt(1)}>← Zurück</button>
                <button className="me-btn me-btn-primary" disabled={busy || !auswahl.length || ohneMail.length > 0 || ohneName.length > 0}
                  onClick={() => setFrage({ text: sid ? `Vorgang #${sid} mit ${auswahl.length} Empfängern aktualisieren?` : `Vorgang mit ${auswahl.length} Empfängern anlegen?`,
                    zeilen: [`Alle ${auswahl.length} bekommen den Brief per E-Mail.`,
                      VARIANTEN.filter(([k]) => jeVariante[k]).map(([k, l]) => `${l}: ${jeVariante[k]}`).join(" · "),
                      anlagen.length ? `Anlage: ${anlagen.map((a) => a.name).join(", ")}` : "Ohne Anlage (kein HR-Auszug gewählt)."], ja: anlegen })}>
                  {sid ? "Vorgang aktualisieren →" : "Vorgang anlegen →"}</button>
              </div>
            </div>
          )}

          {schritt === 3 && sid && (
            <div className="me-drawer-section">
              <h4>Erzeugen & senden</h4>
              <div className="ue-klein" style={{ marginBottom: 8, lineHeight: 1.6 }}>
                Je Empfänger ein eigener Brief (mit Anlage, im Text seiner Variante) per E-Mail; abgelegt wird er im Vorgangsordner.
                Ohne E-Mail-Adresse wird nichts erzeugt — dann zurück zu „Empfänger“ und eintragen.
                {!mailOk && <><br /><b style={{ color: "#f87171" }}>{vorlage?.mail?.hinweis || "Postfach für den Versand fehlt."}</b></>}
              </div>
              <div className="ue-leiste">
                <button className="me-btn me-btn-secondary" onClick={vorschau}><FaFilePdf /> Vorschau aller Briefe</button>
                <button className="me-btn me-btn-primary" disabled={busy || !mailOk}
                  onClick={() => setFrage({ text: "Briefe erzeugen und per E-Mail senden?",
                    zeilen: ["Jeder Empfänger bekommt seinen Brief jetzt per E-Mail (schon gesendete werden übersprungen)."], ja: senden })}>
                  {busy ? "arbeitet …" : <><FaEnvelope /> Erzeugen & senden</>}</button>
                <button className="me-btn me-btn-secondary" onClick={() => setSchritt(2)}>← Text</button>
              </div>
              {ergebnis && (
                <div className="ue-klein" style={{ marginTop: 8 }}>
                  Stand: {ergebnis.stand.mail} per E-Mail gesendet{ergebnis.stand.brief ? ` · ${ergebnis.stand.brief} alter Brief` : ""}
                  {ergebnis.fehler.length > 0 && <div style={{ color: "#f87171" }}>{ergebnis.fehler.join(" · ")}</div>}
                  {(ergebnis.protokoll || []).length > 0 && (
                    <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "grid", gap: 3 }}>
                      {ergebnis.protokoll.map((p, i) => (
                        <li key={i} style={{ color: p.ok ? undefined : "#f59e0b", overflowWrap: "anywhere" }}>{p.ok ? "✓" : "⚠"} {p.text}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {frage && (
            <div className="me-drawer-section" style={{ border: "1px solid #6366f1", borderRadius: 10, padding: 10, background: "rgba(99,102,241,.12)" }}>
              <b>{frage.text}</b>
              {(frage.zeilen || []).filter(Boolean).map((z) => <div key={z} className="ue-klein">{z}</div>)}
              <div className="ue-leiste" style={{ marginTop: 8 }}>
                <button className="me-btn me-btn-primary" onClick={frage.ja}>Ja</button>
                <button className="me-btn me-btn-secondary" onClick={() => setFrage(null)}>Abbrechen</button>
              </div>
            </div>
          )}
          {busy && <div className="ue-klein"><FaSync /> bitte warten …</div>}
        </div>
      </div>
      {vertragNeu && (
        <StammdatenAnlegenDialog startKat="vertraege" vorbelegung={vorbelegungNeu}
          onClose={() => setVertragNeu(null)}
          onGespeichert={() => { setVertragNeu(null); setMeldung("Vertrag angelegt — Liste wird neu geladen."); empfaengerLaden(); }} />
      )}
    </div>
  );
}
